import { Button } from "@/components/ui/button";
import { useUser } from "@/hooks/useUser";
import { useFormik } from "formik";
import {
  TaskColorPaletteEnum,
  userOrgSettingsUpdateSchema,
  userUpdateSchema,
} from "@backend/src/schemas/userSchema";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import ErrorMessage from "@/components/common/ErrorMessage";
import { useEffect, useRef, useState } from "react";
import Select, { SingleValue } from "react-select";
import countries from "@/assets/json/countries.json";
import useUserProfileUpdateMutation from "@/api/mutation/useUserProfileUpdateMutation";
import { isAxiosError } from "axios";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import FormLabel from "@/components/common/FormLabel";
import InputText from "@/components/common/InputText";
import InputSelect from "@/components/common/InputSelect";
// import UserOrganisationCard from "./UserOrganisationCard";
import { Link, useBlocker, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useFileUploadMutation from "@/api/mutation/useFileUploadMutation";
import Spinner from "@/components/ui/spinner";
import UserAvatar from "@/components/ui/userAvatar";
import ChangePassword from "@/components/changePassword/ChangePassword";
import useOrgSettingsUpdateMutation from "@/api/mutation/useOrgSettingsUpdateMutation";
import BackArrow from "../../assets/svg/Arrow.svg";
import UploadSvg from "../../assets/svg/Uploadsvg.svg";
import { useTranslation } from "react-i18next";
import {
  LanguageEnumSchema,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import Dialog from "@/components/common/Dialog";
import { LucideAlertTriangle } from "lucide-react";
import Loader from "@/components/common/Loader";

const countryOptions = countries.map((item) => {
  return { label: item.name, value: item.isoCode };
});

const reactSelectStyle = {
  control: (
    provided: Record<string, unknown>,
    state: { isFocused: boolean }
  ) => ({
    ...provided,
    border: "1px solid #E7E7E7",
    paddingTop: "0.2rem",
    paddingBottom: "0.2rem",
    outline: state.isFocused ? "2px solid #299d91" : "0px solid #E7E7E7",
    boxShadow: state.isFocused ? "0px 0px 0px #299d91" : "none",
    "&:hover": {
      outline: state.isFocused ? "2px solid #299d91" : "0px solid #E7E7E7",
      boxShadow: "0px 0px 0px #299d91",
    },
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean }
  ) => ({
    ...provided,
    color: state.isSelected ? "white" : "black",
    background: state.isSelected ? "#299d91" : "white",
    "&:hover": {
      color: "white",
      background: "#299d9199",
    },
  }),
} as const;

function NewAccountSettings() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { refetch: refetchUser,isRefetching,isFetching } = useCurrentUserQuery();
  const userProfileUpdateMutation = useUserProfileUpdateMutation();
  const [isUserProfileSubmitting, setIsUserProfileSubmitting] = useState(false);

  // const [isopenChangePassword, setIsOpenChangePassword] =
  //   useState<boolean>(false);
  const location = useLocation();
  useEffect(() => {
    if (location.state?.edit) {
      setIsEditable(false);
    }
  }, [location.state?.edit]);

  const [isJobTitleSubmitting, setIsJobTitleSubmitting] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [countryValue, setCountryValue] =
    useState<SingleValue<(typeof countryOptions)[number]>>();
  const [fileUploading, setFileUploading] = useState(false);
  const avatarImg = useRef<HTMLInputElement>(null);
  const useFileUpload = useFileUploadMutation();
  const orgSettingsUpdateMutation = useOrgSettingsUpdateMutation(
    user?.userOrganisation[0]?.userOrganisationId ?? ""
  );
  const navigate = useNavigate();

  const userProfileForm = useFormik<z.infer<typeof userUpdateSchema>>({
    initialValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      country: user?.country ?? "",
      email: user?.email ?? "",
      language: user?.language.toUpperCase() as keyof typeof LanguageEnumSchema,
      isTutorialShow: user?.isTutorialShow ?? false,
    },
    validationSchema: toFormikValidationSchema(userUpdateSchema),
    onSubmit: (values, helper) => {
      setIsUserProfileSubmitting(true);
      userProfileUpdateMutation.mutate(values, {
        onSuccess(data) {
           if (location.state?.edit) {
               window.history.replaceState({}, "");
          }
          toast.success(data.data.message);
          refetchUser().finally(()=>{
            setTimeout(() => {
              setIsUserProfileSubmitting(false);
            }, 500);

          });
        },
        onError(error) {
          if (isAxiosError(error)) {
            if (
              error.response?.status === 400 &&
              error.response.data?.errors &&
              Array.isArray(error.response?.data.errors)
            ) {
              error.response.data.errors.forEach((item) => {
                helper.setFieldError(item.path[0], item.message);
              });
            }
            if (!Array.isArray(error.response?.data.errors)) {
              toast.error(errorKeyFormate(error.response?.data.message + ""));
            }
          }
          setIsUserProfileSubmitting(false);
        },
      });
    },
  });
  useEffect(() => {
    userProfileForm.setValues({
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      country: user?.country ?? "",
      email: user?.email ?? "",
      language: user?.language.toUpperCase() as keyof typeof LanguageEnumSchema,
      isTutorialShow: user?.isTutorialShow ?? false,
    });
  }, [user]);

  const userOrgSettingForm = useFormik<
    z.infer<typeof userOrgSettingsUpdateSchema>
  >({
    initialValues: {
      jobTitle: user?.userOrganisation[0]?.jobTitle ?? "",
      taskColour:
        user?.userOrganisation[0]?.taskColour ?? TaskColorPaletteEnum.BLACK,
    },
    validationSchema: toFormikValidationSchema(userOrgSettingsUpdateSchema),
    onSubmit: (values, helper) => {
      setIsJobTitleSubmitting(true);
      orgSettingsUpdateMutation.mutate(values, {
        onSuccess(data) {
          toast.success(data.data.message);
          setIsJobTitleSubmitting(false);
          refetchUser();
        },
        onError(error) {
          if (isAxiosError(error)) {
            if (
              error.response?.status === 400 &&
              error.response.data?.errors &&
              Array.isArray(error.response?.data.errors)
            ) {
              error.response.data.errors.forEach((item) => {
                helper.setFieldError(item.path[0], item.message);
              });
            }
            if (!Array.isArray(error.response?.data.errors)) {
              toast.error(errorKeyFormate(error.response?.data.message + ""));
            }
          }
          setIsJobTitleSubmitting(false);
        },
      });
    },
  });
  useEffect(() => {
    setCountryValue(countryOptions.find((c) => c.value === user?.country));
  }, [user?.country]);

  const toggleFormReadability = () => {
    setIsEditable((prevState) => !prevState);
    if (!isEditable) {
      refetchUser();
    }
  };

  if (!user) return null;

  const userProfileHasChanges =
    !user.firstName ||
    user.firstName !== userProfileForm.values.firstName ||
    !user.lastName ||
    user.lastName !== userProfileForm.values.lastName ||
    !user.country ||
    user.country !== userProfileForm.values.country ||
    !user.language ||
    user.language !== userProfileForm.values.language;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !userProfileForm.isValid &&
      currentLocation.pathname !== nextLocation.pathname
  );
  function fileUpload(image: File | null) {
    if (image) {
      setFileUploading(true);
      const formdata = new FormData();
      formdata.append("avatarImg", image);
      useFileUpload.mutate(formdata, {
        onSuccess() {
          refetchUser()
            .then(() => {
              setFileUploading(false);
            })
            .catch(() => {
              setFileUploading(false);
            });
        },
        onError(error) {
          if (Array.isArray(error.response?.data.errors)) {
            toast.error(
              error.response?.data.errors[0].message ??
                t("An unexpected error occurred")
            );
          } else {
            toast.error(errorKeyFormate(error.response?.data.message + ""));
          }
          setFileUploading(false);
        },
      });
    }
  }

  // const handleChangePassword = () => {
  //   setIsOpenChangePassword(!isopenChangePassword);
  // };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (userProfileHasChanges) {
      userProfileForm.handleSubmit();
    }
    if (
      user.userOrganisation[0]?.jobTitle !==
        userOrgSettingForm.values.jobTitle &&
      user?.userOrganisation[0]?.organisationId
    ) {
      userOrgSettingForm.handleSubmit();
    }

    if (userProfileForm.isValid && userOrgSettingForm.isValid) {
      setIsEditable(true);
    }
  };
  const getOptions = () => {
    const data = [{ label: "Select job title", value: "" }];
    if (
      user.userOrganisation[0] &&
      user.userOrganisation[0]?.organisation.jobTitlesOfOrg &&
      user.userOrganisation[0]?.organisation.jobTitlesOfOrg.length > 0
    ) {
      user.userOrganisation[0]?.organisation.jobTitlesOfOrg.forEach((s) => {
        data.push({ label: s, value: s });
      });
    }
    return data;
  };

  return (
    <div className="overflow-auto w-full relative">
      {isFetching || isRefetching || userProfileUpdateMutation.isPending &&<Loader className="absolute top-0 left-0  bg-white backdrop-blur z-50" />}
      <div className=" mx-auto p-4 pb-5">
        <div className="text-gray-600 text-lg font-bold mb-4 flex justify-start items-center gap-1">
          <div className="">
            <Button variant={"none"} onClick={() => navigate(-1)}>
              <img src={BackArrow} className="" />
            </Button>
          </div>
          {t("account_settings")}
        </div>
        <div className="flex flex-col gap-5 relative">
          <div className="flex flex-col md:flex-row gap-2 justify-between relative items-center border-2 border-[#000000]/10 p-3 rounded-md w-full">
            <div className="flex flex-col w-full">
              <div>
                <span className="font-bold text-lg ml-4 mt-2">
                  {t("profile_information")}
                </span>
                <br />
                <span className="ml-4 text-sm font-semibold">
                  {t("Update_your_personal_details_here")}
                </span>
              </div>
              <form
                className=" p-5 w-full rounded-2xl"
                onSubmit={(e) => submitForm(e)}
              >
                <div className="flex flex-col md:flex-row items-center gap-2">
                  <div className="w-full mt-2">
                    <FormLabel className="text-gray-450" htmlFor="firstName">
                      {t("first_name")}
                    </FormLabel>
                    <InputText
                      name="firstName"
                      id="firstName"
                      disabled={isEditable}
                      placeholder="Enter first name"
                      value={userProfileForm.values.firstName}
                      onChange={userProfileForm.handleChange}
                      onBlur={userProfileForm.handleBlur}
                      className="mt-0"
                    />
                    {!isEditable && (
                      <div>
                        <ErrorMessage>
                          {userProfileForm.errors.firstName &&
                            userProfileForm.errors.firstName}
                        </ErrorMessage>
                      </div>
                    )}
                  </div>
                  <div className="w-full mt-2">
                    <FormLabel className="text-gray-450" htmlFor="lastName">
                      {t("Last name")}
                    </FormLabel>
                    <InputText
                      name="lastName"
                      id="lastName"
                      disabled={isEditable}
                      placeholder="Enter last name"
                      value={userProfileForm.values.lastName}
                      onChange={userProfileForm.handleChange}
                      onBlur={userProfileForm.handleBlur}
                      className="mt-0"
                    />
                    {!isEditable && (
                      <div>
                        <ErrorMessage>
                          {userProfileForm.errors.lastName &&
                            userProfileForm.errors.lastName}
                        </ErrorMessage>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-col md:flex-row">
                  <div className="w-full mt-2">
                    <FormLabel className="text-gray-450" htmlFor="country">
                      {t("country")}
                    </FormLabel>
                    <InputSelect
                      onChange={(val) => {
                        if (val) {
                          const selectedCountry =
                            val as (typeof countryOptions)[number];
                          const countryValue = selectedCountry?.value;
                          setCountryValue(selectedCountry);
                          userProfileForm.setFieldValue(
                            "country",
                            countryValue
                          );
                        }
                      }}
                      onBlur={userProfileForm.handleBlur}
                      options={countryOptions}
                      isDisabled={isEditable}
                      value={countryValue}
                      placeholder="Select country"
                      className="rounded-xl bbg-primary-500/5 mt-0"
                      name="country"
                    />
                    {!isEditable && (
                      <ErrorMessage>
                        {userProfileForm.errors.country &&
                          userProfileForm.errors.country}
                      </ErrorMessage>
                    )}
                  </div>
                  <div className="w-full mt-2">
                    <FormLabel className="text-gray-450" htmlFor="email">
                      {t("email")}
                    </FormLabel>
                    <InputText
                      name="email"
                      id="email"
                      className="mt-0"
                      placeholder="Enter your email"
                      value={user.email}
                      disabled
                    />
                  </div>
                </div>
                <div className="flex ">
                  {user?.userOrganisation[0]?.organisationId && (
                    <div className="w-1/2 mt-2">
                      <FormLabel className="text-gray-450" htmlFor="jobTitle">
                        {t("job_title")}
                      </FormLabel>
                      <Select
                        name="jobTitle"
                        options={getOptions()}
                        isDisabled={isEditable}
                        onChange={(e) => {
                          if (e) {
                            const selectedCountry =
                              e as (typeof countryOptions)[number];
                            const jobTitle = selectedCountry?.value;
                            userOrgSettingForm.setFieldValue(
                              "jobTitle",
                              jobTitle
                            );
                          }
                        }}
                        value={{
                          label: userOrgSettingForm.values.jobTitle,
                          value: userOrgSettingForm.values.jobTitle,
                        }}
                        menuPlacement="top"
                        styles={reactSelectStyle}
                        className=" w-full "
                      />
                      {!isEditable && (
                        <div>
                          <ErrorMessage>
                            {userOrgSettingForm.errors.jobTitle &&
                              userOrgSettingForm.errors.jobTitle}
                          </ErrorMessage>
                        </div>
                      )}

                      {user.userOrganisation[0]?.role !==
                        UserRoleEnumValue.ADMINISTRATOR &&
                        getOptions().length <= 1 && (
                          <div className="text-xs ">
                            {t(
                              "Please ask your admin to create the list of job titles"
                            )}
                          </div>
                        )}
                      {user.userOrganisation[0]?.role &&
                        user.userOrganisation[0]?.role ==
                          UserRoleEnumValue.ADMINISTRATOR && (
                          <Link
                            className="text-primary-500 text-xs underline underline-offset-1"
                            to={`/organisation/${user?.userOrganisation[0].organisationId}`}
                          >
                            {t("Manage the list of roles")}
                          </Link>
                        )}
                    </div>
                  )}
                  <div className="w-1/2  mt-2">
                    <FormLabel
                      className="text-gray-450  mt-2 ml-2"
                      htmlFor="jobTitle"
                    >
                      {t("language")}
                    </FormLabel>

                    <Select
                      styles={reactSelectStyle}
                      isDisabled={isEditable}
                      className="ml-2 w-full"
                      options={[
                        { label: "English", value: LanguageEnumSchema.EN },
                        { label: "French", value: LanguageEnumSchema.FR },
                      ]}
                      menuPlacement="auto"
                      value={{
                        label:
                          userProfileForm.values.language ===
                          LanguageEnumSchema.EN
                            ? "English"
                            : "French",
                        value: userProfileForm.values.language,
                      }}
                      onChange={(e) => {
                        if (e) {
                          userProfileForm.setFieldValue("language", e.value);
                        }
                      }}
                    />
                  </div>
                </div>
                {/* <div className="flex items-center @2xl:col-span-2 my-4">
                <Button
                  isLoading={isUserProfileSubmitting || isJobTitleSubmitting}
                  variant={"primary"}
                  className="w-auto ml-auto"
                  disabled={isUserProfileSubmitting || isJobTitleSubmitting}
                  type="submit"
                >
                  Save Changes
                </Button>
              </div> */}
                {/* {user.provider?.find((d) => d.providerType === "EMAIL") && (
                  <Button
                    variant="primary_outline"
                    className="transition ease-in-out duration-150 h-auto px-2 py-1 w-[1/2]"
                    onClick={handleChangePassword}
                  >
                    Change Password
                  </Button>
                )} */}
              </form>
            </div>
            <div className="text-center text-gray-700 text-sm space-y-2 flex h-full    flex-col items-center justify-center gap-5 p-1 rounded-2xl w-full">
              <div className="absolute right-8 top-5 flex  gap-2">
                {/* <Button
              className="text-[#ffff] bg-gray-450 p-1 border-2 h-14 w-28 rounded-xl"
              onClick={toggleFormReadability}
            >
              {isEditable ? "Edit" : "Cancel"}
            </Button> */}
                {!location.state?.edit && (
                  <Button
                    size={"sm"}
                    className="text-white bg-gray-450"
                    onClick={toggleFormReadability}
                  >
                    {isEditable ? t("edit") : t("cancel")}
                  </Button>
                )}
                {!isEditable ? (
                  <div className="flex items-center @2xl:col-span-2">
                    <Button
                      isLoading={
                        isUserProfileSubmitting || isJobTitleSubmitting
                      }
                      variant={"primary"}
                      size={"sm"}
                      onClick={submitForm}
                      className="ml-auto"
                      disabled={isUserProfileSubmitting || isJobTitleSubmitting || !userProfileForm.isValid}
                    >
                      {t("update")}
                    </Button>
                  </div>
                ) : (
                  ""
                )}
              </div>
              <div className="w-1/2">
                {user.avatarImg ? (
                  <div className="w-40 h-40 flex justify-center items-center m-auto rounded-full shadow">
                    {fileUploading ? (
                      <div>
                        <Spinner className="h-10 w-36" color="#F99807" />
                      </div>
                    ) : (
                      <img
                        src={user.avatarImg}
                        className="rounded-full m-auto h-full w-full object-fill"
                      />
                    )}
                  </div>
                ) : (
                  <div className="w-40 h-40 flex justify-center items-center m-auto rounded-full shadow">
                    {fileUploading ? (
                      <div>
                        <Spinner className="h-10 w-36" color="#F99807" />
                      </div>
                    ) : (
                      <UserAvatar
                        className="w-full h-full"
                        fontClass="!text-7xl"
                        user={user}
                      ></UserAvatar>
                    )}
                  </div>
                )}
              </div>
              <input
                ref={avatarImg}
                accept="image/jpeg, image/jpg, image/png, image/webp"
                onChange={(e) => {
                  fileUpload(e.target.files ? e.target.files[0] : null);
                }}
                type="file"
                name="avatarImg"
                id=""
                style={{ display: "none" }}
              />
              {!isEditable && (
                <div className="w-1/2 flex gap-5 mr-2">
                  <Button
                    variant="primary_outline"
                    disabled={isEditable}
                    className="transition ease-in-out duration-150 h-auto px-2 py-1 w-full flex flex-col items-center border-2 justify-center gap-1 p-2 bg-primary-500/5 text-white rounded-md border-dotted"
                    onClick={() => avatarImg.current?.click()}
                  >
                    <img src={UploadSvg} />
                    <span className="text-[#000000] text-wrap">
                      {t("Click to Upload or drag and drop")}
                    </span>
                    <span className="text-xs text-[#000000] text-wrap">
                      {t("JPG, PNG, or GIF (Recommended size 1000x1000px)")}
                    </span>
                    {user.avatarImg
                      ? t("change_profile_photo")
                      : t("add_profile_photo")}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {user.provider?.find(
            (d) =>
              d.providerType === "EMAIL" &&
              user.userOrganisation[0]?.role !== UserRoleEnumValue.COLLABORATOR
          ) && (
            <div className="flex flex-col md:flex-row justify-between  border-2 border-[#000000]/10 p-5 rounded-md">
              <div className="flex flex-col w-fsull relative w-full">
                <span className="font-bold text-lg ml-4 mt-2">
                  {t("change_your_password")}
                </span>
                <span className="ml-4 text-sm font-semibold">
                  {t("update_your_password_here")}
                </span>
                <ChangePassword />
              </div>
            </div>
          )}
        </div>
      </div>
      <Dialog
        isOpen={Boolean(blocker.state === "blocked")}
        onClose={() => {
          // blocker.reset();
        }}
        modalClass="rounded-lg w-3/12 text-center"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <LucideAlertTriangle className="w-12 m-auto" />{" "}
          {t("route_block_message")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"primary"}
              onClick={() => {
                blocker.reset && blocker.reset();
              }}
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default NewAccountSettings;
